<script setup lang="ts">
import SectionTitle from '@/components/atoms/SectionTitle.vue'
import FlatButton from '@/components/atoms/FlatButton.vue'
import {
  type TrialViewKollegeUserFragment,
  useCompleteKollegeTrialMutation,
} from './TrialView.generated'
import { computed } from 'vue'
import { KollegeUserTrialStatus, KollegeCourse } from '@/types/schema.generated'
import { format } from 'date-fns'
import { ja } from 'date-fns/locale/ja'
import { useModal } from '@/hooks/useModal'
import ConfirmationModal from '@/components/molecules/ConfirmationModal.vue'
import { useAlert } from '@/libs/alertPortal'
import { useRouter } from 'vue-router'

const props = defineProps<{
  user: TrialViewKollegeUserFragment
}>()

const alertPortal = useAlert()
const router = useRouter()

const { ControlledModal, openModal, closeModal } = useModal()

const { mutate, loading } = useCompleteKollegeTrialMutation({
  variables: {
    id: props.user.id,
  },
})

const trial = computed(() => {
  const filteredTrials = props.user.trials.filter(trial => {
    return trial.course === KollegeCourse.Standardv20250418
  })
  if (filteredTrials.length === 0) {
    return null
  }
  return filteredTrials[filteredTrials.length - 1]
})

const trialStatus = computed(() => {
  if (!trial.value) {
    return null
  }
  switch (trial.value.status) {
    case KollegeUserTrialStatus.Booked:
      return '予約済み'
    case KollegeUserTrialStatus.Completed:
      return '受講済み'
    case KollegeUserTrialStatus.UnBooked:
      return '未予約'
    default:
      return null
  }
})

const trialDate = computed(() => {
  if (!trial.value?.schedule?.startsAt) {
    return null
  }
  const date = new Date(trial.value.schedule.startsAt)
  return format(date, 'yyyy/MM/dd(E) HH:mm', {
    locale: ja,
  })
})

const trialMeetURL = computed(() => {
  if (!trial.value?.schedule?.videoUrl) {
    return null
  }
  return trial.value.schedule.videoUrl
})

const completeTrial = async () => {
  if (!trial.value) {
    return
  }
  const result = await mutate({
    id: trial.value.id,
  })
  if (result?.errors) {
    alertPortal.error(result?.errors[0]?.message)
    return
  }

  alertPortal.success('無料体験会受講済みを確認しました。')

  // 色々めんどくさいのでもうリロードしちゃう
  router.go(0)
}
const submitCompleteTrial = () => {
  completeTrial()
  closeModal()
}

const name = computed(() => {
  if (!props.user.profile) {
    return '名前なしユーザー'
  }
  return `${props.user.profile.familyName} ${props.user.profile.givenName}`
})
</script>
<template>
  <section v-if="trial" :class="$style.main">
    <SectionTitle :class="$style.title">無料体験会</SectionTitle>
    <dl :class="$style.infos">
      <div :class="$style.info">
        <dt>受講状況</dt>
        <dd>
          {{ trialStatus }}
        </dd>
      </div>
      <div v-if="trialDate" :class="$style.info">
        <dt>受講日時(予約日時)</dt>
        <dd>
          {{ trialDate }}
        </dd>
      </div>
      <div v-if="trialMeetURL" :class="$style.info">
        <dt>Google Meet</dt>
        <dd>
          <a :href="trialMeetURL" target="_blank" rel="noopener noreferrer">
            {{ trialMeetURL }}
          </a>
        </dd>
      </div>
    </dl>
    <FlatButton
      v-if="trial.status !== KollegeUserTrialStatus.Completed"
      :class="$style.saveButton"
      type="submit"
      :disabled="loading"
      @click="openModal"
    >
      無料体験会受講済を確認
    </FlatButton>
    <component :is="ControlledModal">
      <ConfirmationModal
        title="該当ユーザーの体験会受講済みを確認"
        buttonText="無料体験会受講済みを確認"
        @confirm="submitCompleteTrial"
        @close="closeModal"
      >
        <p>
          <b>{{ name }}</b
          >様の無料体験会受講済を確認します。よろしいですか？<br />
          訂正できませんので、注意してください。
        </p>
      </ConfirmationModal>
    </component>
  </section>
</template>

<style lang="scss" module>
.title {
  padding-bottom: 24px;
  border-bottom: 1px solid $color-border-low-emphasis;
}

.main {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.infos {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}

.info {
  display: flex;
  flex-direction: column;
  gap: 4px;

  dt {
    font-weight: bold;
  }
}
</style>
